import { db } from "../firebase";
import {
	collection,
	doc,
	setDoc,
	getDoc,
	getDocs,
	query,
	where,
	orderBy,
	limit,
	serverTimestamp,
	onSnapshot,
	Timestamp,
} from "firebase/firestore";
import {
	getUserBioFlowSensors,
	getBioFlowSensorData,
	subscribeBioFlowSensorData,
	getBioFlowSensorChartData,
	registerBioFlowSensorToUser,
	removeBioFlowSensorFromUser,
	updateBioFlowSensorSettings,
} from "./bioFlowSensorService";

/**
 * Silo Sensor Service
 * Handles all Firestore operations for ESP32 DHT11 sensor data
 */

// Collection names
const SILO_SENSORS_COLLECTION = "SiloSensors";
const USER_SENSORS_COLLECTION = "UserSiloSensors";
const USERS_COLLECTION = "Users";
const LITTER_GUARDS_SUBCOLLECTION = "LitterGuards";

const debugLog = () => {};

const parseDocIdTimestamp = (docId) => {
        if (!docId || typeof docId !== "string") return null;

        const isoMatch = docId.match(
                /^(\d{4}-\d{2}-\d{2})_(\d{2})-(\d{2})-(\d{2})Z$/
        );

        if (!isoMatch) return null;

        const [, datePart, hours, minutes, seconds] = isoMatch;
        const isoString = `${datePart}T${hours}:${minutes}:${seconds}Z`;
        const parsed = new Date(isoString);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const normalizeTimestamp = (value, docId = null) => {
        if (!value) {
                return parseDocIdTimestamp(docId);
        }

        if (value instanceof Date) return value;
        if (typeof value.toDate === "function") return value.toDate();
        if (typeof value === "number") {
                const parsed = new Date(value);
                return Number.isNaN(parsed.getTime()) ? null : parsed;
        }
        if (typeof value === "object" && value !== null) {
                if (typeof value.seconds === "number") {
                        return new Date(value.seconds * 1000);
                }
        }
        if (typeof value === "string") {
                const parsed = new Date(value);
                if (!Number.isNaN(parsed.getTime())) {
                        return parsed;
                }
        }

        return parseDocIdTimestamp(docId);
};

/**
 * Register a sensor to a user
 * @param {string} userId - User ID
 * @param {string} deviceId - Device MAC address
 * @param {string} sensorName - User-friendly name for the sensor
 * @param {string} sensorType - Type of sensor (silo, water, temperature, litterGuard)
 * @returns {Promise<void>}
 */
export const registerSensorToUser = async (
        userId,
        deviceId,
        sensorName,
        sensorType = "silo",
        metadata = {}
) => {
        try {
                // Add to UserSiloSensors collection for easy user-to-sensor mapping
                const userSensorRef = doc(
                        db,
                        USER_SENSORS_COLLECTION,
                        `${userId}_${deviceId}`
                );
                const preparedMetadata = { ...metadata };

                if (preparedMetadata.installationDate instanceof Date) {
                        preparedMetadata.installationDate = Timestamp.fromDate(
                                preparedMetadata.installationDate
                        );
                }

                await setDoc(
                        userSensorRef,
                        {
                                userId,
                                deviceId,
                                sensorName,
                                type: sensorType,
                                registeredAt: serverTimestamp(),
                                isActive: true,
                                ...preparedMetadata,
                        },
                        { merge: true }
                );

                if (sensorType === "litterGuard") {
			// Use mobile app LitterGuard structure - store in user's subcollection
			const litterGuardRef = doc(db, USERS_COLLECTION, userId, LITTER_GUARDS_SUBCOLLECTION, deviceId);
			await setDoc(
				litterGuardRef,
				{
					MAC: deviceId,
					Name: sensorName,
					LastSeen: serverTimestamp(),
					Active: true,
					CurrentTemperature: 0, // Will be updated when data arrives
				},
				{ merge: true }
			);
                } else if (sensorType === "water") {
                        // Delegate to BioFlow sensor service
                        await registerBioFlowSensorToUser(
                                userId,
                                deviceId,
                                sensorName,
                                metadata
                        );
                } else {
                        // Use existing structure for other sensor types
                        const sensorRef = doc(db, SILO_SENSORS_COLLECTION, deviceId);
			await setDoc(
				sensorRef,
				{
					ownerId: userId,
					sensorName,
					type: sensorType,
					registeredAt: serverTimestamp(),
				},
				{ merge: true }
			);
		}

		debugLog(
			`Sensor ${deviceId} (type: ${sensorType}) registered to user ${userId}`
		);
	} catch (error) {
		console.error("Error registering sensor:", error);
		throw error;
	}
};

/**
 * Get all sensors for a user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of sensor objects
 */
export const getUserSensors = async (userId) => {
	try {
		const q = query(
			collection(db, USER_SENSORS_COLLECTION),
			where("userId", "==", userId),
			where("isActive", "==", true)
		);

		const querySnapshot = await getDocs(q);
		const sensors = [];

		for (const docSnapshot of querySnapshot.docs) {
			const sensorData = docSnapshot.data();

                        if (sensorData.type === "litterGuard") {
                                // Get LitterGuard data from user's subcollection
                                const litterGuardRef = doc(db, USERS_COLLECTION, userId, LITTER_GUARDS_SUBCOLLECTION, sensorData.deviceId);
                                const litterGuardDoc = await getDoc(litterGuardRef);

                                if (litterGuardDoc.exists()) {
					const litterGuardData = litterGuardDoc.data();
					// Map mobile app LitterGuard structure to web app format
					sensors.push({
						...sensorData,
						id: docSnapshot.id,
						sensorName: litterGuardData.Name || sensorData.sensorName,
						lastUpdated: litterGuardData.LastSeen,
						active: litterGuardData.Active,
						currentTemperature: litterGuardData.CurrentTemperature,
						mac: litterGuardData.MAC,
					});
				} else {
					sensors.push({
						...sensorData,
						id: docSnapshot.id,
						lastUpdated: null,
					});
				}
                        } else if (sensorData.type === "water") {
                                // Delegate to BioFlow sensor service - skip here, will be merged later
                                continue;
                        } else {
                                // Get the latest status from SiloSensors collection
                                const sensorRef = doc(db, SILO_SENSORS_COLLECTION, sensorData.deviceId);
                                const sensorDoc = await getDoc(sensorRef);

				if (sensorDoc.exists()) {
					sensors.push({
						...sensorData,
						...sensorDoc.data(),
						id: docSnapshot.id,
					});
				} else {
					sensors.push({
						...sensorData,
						id: docSnapshot.id,
						lastUpdated: null,
					});
				}
			}
		}

		// Get BioFlow sensors and merge with other sensors
		const bioFlowSensors = await getUserBioFlowSensors(userId);
		sensors.push(...bioFlowSensors);

		return sensors;
	} catch (error) {
		console.error("Error getting user sensors:", error);
		throw error;
	}
};

/**
 * Get latest sensor data
 * @param {string} deviceId - Device ID
 * @param {number} limitCount - Number of recent readings to fetch
 * @param {string} sensorType - Type of sensor
 * @param {string} userId - User ID (required for LitterGuard sensors)
 * @returns {Promise<Array>} Array of sensor data
 */
export const getSensorData = async (
        deviceId,
        limitCount = 50,
        sensorType = "silo",
        userId = null
) => {
        try {
                // Use Litter Guard specific function if sensor type is litterGuard
                if (sensorType === "litterGuard") {
                        if (!userId) {
                                throw new Error("UserId is required for LitterGuard sensors");
                        }
                        return await getLitterGuardData(userId, deviceId, limitCount);
                }

                if (sensorType === "water") {
                        // Delegate to BioFlow sensor service
                        return await getBioFlowSensorData(deviceId, limitCount);
                }

                const dataRef = collection(db, SILO_SENSORS_COLLECTION, deviceId, "Data");
                const q = query(dataRef, orderBy("timestamp", "desc"), limit(limitCount));

                const querySnapshot = await getDocs(q);
                const data = [];

                querySnapshot.forEach((docSnapshot) => {
                        const docData = docSnapshot.data();
                        data.push({
                                id: docSnapshot.id,
                                ...docData,
                                timestamp: normalizeTimestamp(
                                        docData.timestamp,
                                        docSnapshot.id
                                ),
                        });
                });

                return data;
        } catch (error) {
                console.error("Error getting sensor data:", error);
		throw error;
	}
};

/**
 * Subscribe to real-time sensor data updates
 * @param {string} deviceId - Device ID
 * @param {function} callback - Callback function for updates
 * @param {string} sensorType - Type of sensor
 * @param {string} userId - User ID (required for LitterGuard sensors)
 * @returns {function} Unsubscribe function
 */
export const subscribeSensorData = (
        deviceId,
        callback,
        sensorType = "silo",
        userId = null
) => {
        // Use Litter Guard specific function if sensor type is litterGuard
        if (sensorType === "litterGuard") {
                if (!userId) {
                        console.error("UserId is required for LitterGuard sensors");
                        return () => {};
                }
                return subscribeLitterGuardData(userId, deviceId, callback);
        }

        if (sensorType === "water") {
                // Delegate to BioFlow sensor service
                return subscribeBioFlowSensorData(deviceId, callback);
        }

        const dataRef = collection(db, SILO_SENSORS_COLLECTION, deviceId, "Data");
        const q = query(dataRef, orderBy("timestamp", "desc"), limit(1));

        return onSnapshot(
                q,
                (querySnapshot) => {
                        if (!querySnapshot.empty) {
                                const latestDoc = querySnapshot.docs[0];
                                const latestData = latestDoc.data();
                                callback({
                                        id: latestDoc.id,
                                        ...latestData,
                                        timestamp: normalizeTimestamp(
                                                latestData.timestamp,
                                                latestDoc.id
                                        ),
                                });
                        }
                },
                (error) => {
                        console.error("Error in sensor data subscription:", error);
		}
	);
};

/**
 * Get sensor errors
 * @param {string} deviceId - Device ID
 * @param {number} limitCount - Number of recent errors to fetch
 * @returns {Promise<Array>} Array of error logs
 */
export const getSensorErrors = async (deviceId, limitCount = 20) => {
	try {
		const errorsRef = collection(db, SILO_SENSORS_COLLECTION, deviceId, "Errors");
		const q = query(errorsRef, orderBy("timestamp", "desc"), limit(limitCount));

		const querySnapshot = await getDocs(q);
		const errors = [];

		querySnapshot.forEach((docSnapshot) => {
			errors.push({
				id: docSnapshot.id,
				...docSnapshot.data(),
			});
		});

		return errors;
	} catch (error) {
		console.error("Error getting sensor errors:", error);
		return [];
	}
};

/**
 * Remove sensor from user
 * @param {string} userId - User ID
 * @param {string} deviceId - Device ID
 * @param {string} sensorType - Type of sensor
 * @returns {Promise<void>}
 */
export const removeSensorFromUser = async (
	userId,
	deviceId,
	sensorType = "silo"
) => {
	try {
		const userSensorRef = doc(
			db,
			USER_SENSORS_COLLECTION,
			`${userId}_${deviceId}`
		);
		await setDoc(
			userSensorRef,
			{
				isActive: false,
				removedAt: serverTimestamp(),
			},
			{ merge: true }
		);

                if (sensorType === "litterGuard") {
                        // Remove LitterGuard from user's subcollection
                        const litterGuardRef = doc(db, USERS_COLLECTION, userId, LITTER_GUARDS_SUBCOLLECTION, deviceId);
                        await setDoc(
                                litterGuardRef,
                                {
                                        Active: false,
                                        removedAt: serverTimestamp(),
                                },
                                { merge: true }
                        );
                } else if (sensorType === "water") {
                        // Delegate to BioFlow sensor service
                        await removeBioFlowSensorFromUser(userId, deviceId);
                } else {
                        // Remove user ownership from SiloSensor
                        const sensorRef = doc(db, SILO_SENSORS_COLLECTION, deviceId);
                        await setDoc(
                                sensorRef,
				{
					ownerId: null,
					removedAt: serverTimestamp(),
				},
				{ merge: true }
			);
		}

		debugLog(`Sensor ${deviceId} removed from user ${userId}`);
	} catch (error) {
		console.error("Error removing sensor:", error);
		throw error;
	}
};

/**
 * Get aggregated sensor data for charts
 * @param {string} deviceId - Device ID
 * @param {Date} startDate - Start date for data range
 * @param {Date} endDate - End date for data range
 * @returns {Promise<Array>} Array of sensor data for charting
 */
export const getSensorChartData = async (
        deviceId,
        startDate,
        endDate,
        sensorType = "silo"
) => {
        try {
                debugLog("Fetching sensor chart data:", {
                        deviceId,
                        startDate,
                        endDate,
                });

                if (sensorType === "water") {
                        // Delegate to BioFlow sensor service
                        return await getBioFlowSensorChartData(deviceId, startDate, endDate);
                }

                const dataRef = collection(db, SILO_SENSORS_COLLECTION, deviceId, "Data");

                // Try the optimized query first
                try {
                        const q = query(
                                dataRef,
                                where("timestamp", ">=", Timestamp.fromDate(startDate)),
				where("timestamp", "<=", Timestamp.fromDate(endDate)),
				orderBy("timestamp", "asc")
			);

			const querySnapshot = await getDocs(q);
			const data = [];

			debugLog(
				`Found ${querySnapshot.size} documents for sensor ${deviceId}`
			);

                                querySnapshot.forEach((docSnapshot) => {
                                        const docData = docSnapshot.data();
                                        const timestamp = normalizeTimestamp(
                                                docData.timestamp,
                                                docSnapshot.id
                                        );

                                        if (!timestamp) return;

                                        const dataPoint = {
                                                timestamp,
                                                sensor1Temperature: docData.sensor1?.temperature || 0,
                                                sensor1Humidity: docData.sensor1?.humidity || 0,
                                                sensor2Temperature: docData.sensor2?.temperature || 0,
                                                sensor2Humidity: docData.sensor2?.humidity || 0,
                                        };
                                        data.push(dataPoint);
                                });

			debugLog("Processed sensor chart data:", data.length, "points");
			if (data.length > 0) {
				debugLog("Sample data point:", data[0]);
			}

			return data;
		} catch (queryError) {
			// If the query fails (likely due to missing index), try a simpler approach
			console.warn(
				"Composite query failed, trying simpler approach:",
				queryError
			);

			// Get all data ordered by timestamp and filter in memory
			const simpleQuery = query(
				dataRef,
				orderBy("timestamp", "desc"),
				limit(1000)
			);
			const querySnapshot = await getDocs(simpleQuery);
			const data = [];

			const startTime = startDate.getTime();
			const endTime = endDate.getTime();

			querySnapshot.forEach((docSnapshot) => {
                                        const docData = docSnapshot.data();
                                        const timestamp = normalizeTimestamp(
                                                docData.timestamp,
                                                docSnapshot.id
                                        );

                                        // Filter by date range in memory
                                        if (
                                                timestamp &&
                                                timestamp.getTime() >= startTime &&
                                                timestamp.getTime() <= endTime
                                        ) {
                                                const dataPoint = {
                                                        timestamp,
                                                        sensor1Temperature: docData.sensor1?.temperature || 0,
                                                        sensor1Humidity: docData.sensor1?.humidity || 0,
						sensor2Temperature: docData.sensor2?.temperature || 0,
						sensor2Humidity: docData.sensor2?.humidity || 0,
					};
					data.push(dataPoint);
				}
			});

			// Sort by timestamp ascending
			data.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

			debugLog(
				"Processed sensor chart data (fallback):",
				data.length,
				"points"
			);
			if (data.length > 0) {
				debugLog("Sample data point:", data[0]);
			}

			return data;
		}
	} catch (error) {
		console.error("Error getting sensor chart data:", error);
		throw error;
	}
};

/**
 * Get sensor settings from user's sensor document
 * @param {string} userId - User ID
 * @param {string} deviceId - Device ID
 * @returns {Promise<Object>} Sensor settings
 */
export const getSensorSettings = async (userId, deviceId) => {
	try {
		const userSensorRef = doc(
			db,
			USER_SENSORS_COLLECTION,
			`${userId}_${deviceId}`
		);
		const settingsDoc = await getDoc(userSensorRef);

		if (settingsDoc.exists()) {
			const data = settingsDoc.data();
			return {
				sensorName: data.sensorName,
				thresholds: data.thresholds,
				notificationsEnabled: data.notificationsEnabled,
			};
		}
		return null;
	} catch (error) {
		console.error("Error getting sensor settings:", error);
		throw error;
	}
};

/**
 * Update sensor settings in user's sensor document
 * @param {string} userId - User ID
 * @param {string} deviceId - Device ID
 * @param {string} sensorName - Sensor name
 * @param {Object} thresholds - Threshold settings for warnings
 * @param {boolean} notificationsEnabled - Whether notifications are enabled
 * @param {string} sensorType - Type of sensor
 * @returns {Promise<void>}
 */
export const updateSensorSettings = async (
	userId,
	deviceId,
	sensorName,
	thresholds,
	notificationsEnabled = true,
	sensorType = "silo"
) => {
	try {
		// Update everything in UserSiloSensors collection
		const userSensorRef = doc(
			db,
			USER_SENSORS_COLLECTION,
			`${userId}_${deviceId}`
		);
		await setDoc(
			userSensorRef,
			{
				sensorName,
				thresholds,
				notificationsEnabled,
				updatedAt: serverTimestamp(),
			},
			{ merge: true }
		);

		if (sensorType === "litterGuard") {
			// Update LitterGuard name in user's subcollection
			const litterGuardRef = doc(db, USERS_COLLECTION, userId, LITTER_GUARDS_SUBCOLLECTION, deviceId);
			await setDoc(
				litterGuardRef,
				{
					Name: sensorName,
					updatedAt: serverTimestamp(),
				},
				{ merge: true }
			);
		} else if (sensorType === "water") {
			// Delegate to BioFlow sensor service
			await updateBioFlowSensorSettings(
				userId,
				deviceId,
				sensorName,
				thresholds,
				notificationsEnabled
			);
		} else {
			// Update sensor name in SiloSensors collection
			const sensorRef = doc(db, SILO_SENSORS_COLLECTION, deviceId);
			await setDoc(
				sensorRef,
				{
					sensorName,
					updatedAt: serverTimestamp(),
				},
				{ merge: true }
			);
		}

		debugLog(`Sensor settings updated for ${deviceId}`);
	} catch (error) {
		console.error("Error updating sensor settings:", error);
		throw error;
	}
};

/**
 * Get Litter Guard sensor data
 * @param {string} userId - User ID
 * @param {string} macAddress - Device MAC address
 * @param {number} limitCount - Number of recent readings to fetch
 * @returns {Promise<Array>} Array of sensor data
 */
export const getLitterGuardData = async (userId, macAddress, limitCount = 50) => {
	try {
		// Get the Day document from Sensordata subcollection under user's LitterGuards
		const dataRef = doc(db, USERS_COLLECTION, userId, LITTER_GUARDS_SUBCOLLECTION, macAddress, "Sensordata", "Day");
		const dataDoc = await getDoc(dataRef);

		if (!dataDoc.exists()) {
			return [];
		}

		const data = dataDoc.data();
		const sensorData = data.Sensordata || [];

		// Sort by timestamp descending and limit results
		const sortedData = sensorData
			.sort((a, b) => b.t - a.t)
			.slice(0, limitCount)
			.map((item) => ({
				id: `${macAddress}_${item.t}`,
				timestamp: item.t,
				temperature: item.c, // Temperature field in mobile app structure
				t: item.t,
				c: item.c,
				...item, // Include any additional fields
			}));

		return sortedData;
	} catch (error) {
		console.error("Error getting Litter Guard data:", error);
		throw error;
	}
};

/**
 * Subscribe to real-time Litter Guard data updates
 * @param {string} userId - User ID
 * @param {string} macAddress - Device MAC address
 * @param {function} callback - Callback function for updates
 * @returns {function} Unsubscribe function
 */
export const subscribeLitterGuardData = (userId, macAddress, callback) => {
	const dataRef = doc(db, USERS_COLLECTION, userId, LITTER_GUARDS_SUBCOLLECTION, macAddress, "Sensordata", "Day");

	return onSnapshot(
		dataRef,
		(docSnapshot) => {
			if (docSnapshot.exists()) {
				const data = docSnapshot.data();
				const sensorData = data.Sensordata || [];
				
				if (sensorData.length > 0) {
					// Get the most recent reading
					const latestReading = sensorData.sort((a, b) => b.t - a.t)[0];
					callback({
						id: `${macAddress}_${latestReading.t}`,
						timestamp: latestReading.t,
						temperature: latestReading.c,
						t: latestReading.t,
						c: latestReading.c,
						...latestReading,
					});
				}
			}
		},
		(error) => {
			console.error("Error in Litter Guard data subscription:", error);
		}
	);
};

/**
 * Get Litter Guard chart data for visualization
 * @param {string} userId - User ID
 * @param {string} macAddress - Device MAC address
 * @param {Date} startDate - Start date for data range
 * @param {Date} endDate - End date for data range
 * @returns {Promise<Array>} Array of sensor data for charting
 */
export const getLitterGuardChartData = async (userId, macAddress, startDate, endDate) => {
	try {
		debugLog("Fetching Litter Guard chart data:", {
			userId,
			macAddress,
			startDate,
			endDate,
		});

		// Get the Day document from Sensordata subcollection under user's LitterGuards
		const dataRef = doc(db, USERS_COLLECTION, userId, LITTER_GUARDS_SUBCOLLECTION, macAddress, "Sensordata", "Day");
		const dataDoc = await getDoc(dataRef);

		if (!dataDoc.exists()) {
			return [];
		}

		const data = dataDoc.data();
		const sensorData = data.Sensordata || [];

		const startTime = startDate.getTime();
		const endTime = endDate.getTime();

		// Filter data by date range and convert to chart format
		const filteredData = sensorData
			.filter((item) => {
				const timestamp = item.t;
				return timestamp >= startTime && timestamp <= endTime;
			})
			.sort((a, b) => a.t - b.t)
			.map((item) => ({
				timestamp: new Date(item.t),
				temperature: item.c || 0,
			}));

		debugLog("Processed Litter Guard chart data:", filteredData.length, "points");
		return filteredData;
	} catch (error) {
		console.error("Error getting Litter Guard chart data:", error);
		throw error;
	}
};
